import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { SyllabusAPI } from "@/lib/syllabus-api";
import { InstitutionsAPI, CampusesAPI } from "@/lib/sprint2-api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  BookOpenText, Upload, Loader2, Trash2, FileText, ChevronsUpDown, X, Search, Calendar,
  Sparkles, CheckCircle2, AlertCircle, Clock,
} from "lucide-react";
import { toast } from "sonner";

const UPLOAD_ROLES = ["super_admin", "inst_admin", "principal", "campus_admin", "hod", "coordinator", "it_admin"];

const fmtSize = (n) => {
  if (!n) return "—";
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n > 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
};

export default function Syllabus() {
  const { user } = useAuth();
  const canUpload = (user?.roles || []).some((r) => UPLOAD_ROLES.includes(r));
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [filterBoard, setFilterBoard] = useState("all");

  const boardsQ = useQuery({ queryKey: ["syllabus-boards"], queryFn: () => SyllabusAPI.boards() });
  const listQ = useQuery({
    queryKey: ["syllabus-list", filterBoard],
    queryFn: () => SyllabusAPI.list(filterBoard === "all" ? {} : { board: filterBoard }),
  });

  const filtered = useMemo(() => {
    const rows = listQ.data?.syllabi || [];
    if (!search.trim()) return rows;
    const s = search.toLowerCase();
    return rows.filter((r) =>
      (r.title || "").toLowerCase().includes(s) ||
      (r.subject || "").toLowerCase().includes(s) ||
      (r.grade || "").toLowerCase().includes(s) ||
      (r.filename || "").toLowerCase().includes(s)
    );
  }, [listQ.data, search]);

  const removeRow = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await SyllabusAPI.remove(row.id);
      toast.success("Syllabus deleted");
      qc.invalidateQueries({ queryKey: ["syllabus-list"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <AppShell>
      <div className="p-6 max-w-7xl mx-auto" data-testid="syllabus-page">
        <div className="mb-6">
          <h1 className="font-display font-extrabold tracking-tight text-3xl flex items-center gap-2.5">
            <BookOpenText className="h-7 w-7 text-primary" /> Syllabus
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
            Upload board-tagged syllabus documents per institution &amp; campus. The Curriculum module
            picks these up to auto-build per-class curricula.
          </p>
        </div>

        {canUpload && (
          <UploadTabs
            boards={boardsQ.data}
            onSingleDone={() => qc.invalidateQueries({ queryKey: ["syllabus-list"] })}
            onBulkDone={() => {
              qc.invalidateQueries({ queryKey: ["syllabus-list"] });
              qc.invalidateQueries({ queryKey: ["syllabus-extractions"] });
            }}
          />
        )}

        <ExtractionsPanel qcKey="syllabus-extractions" />

        {/* List */}
        <div className="bg-card border border-border rounded-2xl shadow-sm p-5" data-testid="syllabus-list">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <h2 className="font-display font-bold text-lg flex-1">Uploaded syllabi</h2>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search title, grade, subject…"
                className="pl-8 h-8 text-xs w-[260px]"
                data-testid="syllabus-search"
              />
            </div>
            <Select value={filterBoard} onValueChange={setFilterBoard}>
              <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="syllabus-board-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All boards</SelectItem>
                {boardsQ.data?.flat?.map((b) => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {listQ.isLoading ? (
            <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
              No syllabi uploaded yet.
              {canUpload && <div className="mt-1 text-xs">Use the form above to upload your first one.</div>}
            </div>
          ) : (
            <div className="divide-y divide-border border border-border rounded-md overflow-hidden">
              {filtered.map((row) => (
                <div key={row.id} className="px-4 py-3 hover:bg-muted/40 flex items-start gap-3" data-testid={`syllabus-row-${row.id}`}>
                  <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <a
                        href={row.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-sm hover:underline truncate"
                        data-testid={`syllabus-title-${row.id}`}
                      >
                        {row.title}
                      </a>
                      {row.grade && <Badge variant="outline" className="text-[10px]">{row.grade}</Badge>}
                      {row.subject && <Badge variant="outline" className="text-[10px]">{row.subject}</Badge>}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                      <span>{row.filename}</span>
                      <span>·</span>
                      <span>{fmtSize(row.size)}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtDate(row.uploaded_at)}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                      {row.board_labels?.map((label, i) => (
                        <Badge key={i} className="text-[10px] bg-sky-500/10 text-sky-700 hover:bg-sky-500/15 border-0">{label.split(" — ")[0]}</Badge>
                      ))}
                    </div>
                  </div>
                  {canUpload && (
                    <Button size="icon" variant="ghost" onClick={() => removeRow(row)} title="Delete" data-testid={`syllabus-delete-${row.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function UploadCard({ boards, onDone }) {
  const fileRef = useRef(null);
  const [institutionId, setInstitutionId] = useState("");
  const [campusIds, setCampusIds] = useState([]);
  const [selectedBoards, setSelectedBoards] = useState([]);
  const [title, setTitle] = useState("");
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [chapter, setChapter] = useState("");
  const [topics, setTopics] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const instQ = useQuery({ queryKey: ["syl-insts"], queryFn: () => InstitutionsAPI.list() });
  const campusQ = useQuery({
    queryKey: ["syl-campuses", institutionId],
    queryFn: () => CampusesAPI.list({ institution_id: institutionId }),
    enabled: !!institutionId,
  });

  const canSubmit =
    selectedBoards.length > 0 &&
    !!institutionId &&
    !!title.trim() &&
    !!file;

  const toggleCampus = (id) => {
    setCampusIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || busy) return;
    const fd = new FormData();
    fd.append("institution_id", institutionId);
    fd.append("boards", selectedBoards.join(","));
    fd.append("campus_ids", campusIds.join(","));
    fd.append("title", title.trim());
    if (grade.trim()) fd.append("grade", grade.trim());
    if (subject.trim()) fd.append("subject", subject.trim());
    if (notes.trim()) fd.append("notes", notes.trim());
    fd.append("file", file);
    setBusy(true);
    try {
      await SyllabusAPI.upload(fd);
      toast.success("Syllabus uploaded");
      // Reset
      setTitle(""); setGrade(""); setSubject(""); setNotes("");
      setSelectedBoards([]); setCampusIds([]); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border rounded-2xl shadow-sm p-5 mb-6 space-y-4" data-testid="syllabus-upload-card">
      <div className="flex items-center gap-2 mb-1">
        <Upload className="h-4 w-4 text-primary" />
        <h2 className="font-display font-bold text-lg">Upload syllabus</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Boards multi-select */}
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">
            1. Boards <span className="text-rose-600">*</span>
          </Label>
          <BoardsMultiSelect
            groups={boards?.groups || {}}
            value={selectedBoards}
            onChange={setSelectedBoards}
          />
        </div>

        {/* Institution */}
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">
            2. Institution <span className="text-rose-600">*</span>
          </Label>
          <Select value={institutionId} onValueChange={(v) => { setInstitutionId(v); setCampusIds([]); }}>
            <SelectTrigger className="h-10" data-testid="syl-institution-select">
              <SelectValue placeholder={instQ.isLoading ? "Loading…" : "Select institution"} />
            </SelectTrigger>
            <SelectContent>
              {(instQ.data || []).map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Campuses multi (filtered) */}
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">
            3. Campuses <span className="text-muted-foreground text-[10px] font-normal">(optional · default = all)</span>
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-10 w-full justify-between font-normal"
                disabled={!institutionId || campusQ.isLoading}
                data-testid="syl-campuses-select"
              >
                <span className="truncate text-sm">
                  {!institutionId ? "Select institution first" :
                   campusIds.length === 0 ? "All campuses" :
                   `${campusIds.length} campus${campusIds.length > 1 ? "es" : ""} selected`}
                </span>
                <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] max-h-[280px] overflow-y-auto p-1">
              {(campusQ.data || []).length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                  {institutionId ? "No campuses" : "Select institution first"}
                </div>
              ) : (
                (campusQ.data || []).map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/60 rounded cursor-pointer"
                    data-testid={`syl-campus-option-${c.id}`}
                  >
                    <Checkbox checked={campusIds.includes(c.id)} onCheckedChange={() => toggleCampus(c.id)} />
                    <span className="text-sm truncate">{c.name}{c.city ? ` · ${c.city}` : ""}</span>
                  </label>
                ))
              )}
            </PopoverContent>
          </Popover>
          {campusIds.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2" data-testid="syl-campus-chips">
              {campusIds.map((id) => {
                const c = (campusQ.data || []).find((x) => x.id === id);
                return (
                  <Badge key={id} variant="outline" className="text-[10px] gap-1 pr-1">
                    {c?.name?.split(",")[0] || "Campus"}
                    <button type="button" onClick={() => toggleCampus(id)}><X className="h-3 w-3" /></button>
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">Grade</Label>
          <Input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="Grade 10" data-testid="syl-grade" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">Subject</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Science" data-testid="syl-subject" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">Chapter</Label>
          <Input value={chapter} onChange={(e) => setChapter(e.target.value)} placeholder="Ch 1 — Light" data-testid="syl-chapter" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">Topics</Label>
          <Input value={topics} onChange={(e) => setTopics(e.target.value)} placeholder="Reflection, Refraction…" data-testid="syl-topics" />
        </div>
      </div>

      <div>
        <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">Title <span className="text-rose-600">*</span></Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Grade 10 Science — CBSE 2025-26" data-testid="syl-title" />
      </div>

      <div>
        <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">Notes</Label>
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes for curriculum mapping…" data-testid="syl-notes" />
      </div>

      <div className="flex items-end gap-3 flex-wrap pt-1 border-t border-border">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">
            File <span className="text-rose-600">*</span>
            <span className="text-muted-foreground text-[10px] font-normal ml-1">(PDF / DOC / DOCX · max 25MB)</span>
          </Label>
          <Input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="syl-file"
            className="h-10"
          />
          {file && <div className="text-[11px] text-muted-foreground mt-1">{file.name} · {fmtSize(file.size)}</div>}
        </div>
        <Button type="submit" disabled={!canSubmit || busy} className="h-10" data-testid="syl-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
          Upload
        </Button>
      </div>
    </form>
  );
}

function BoardsMultiSelect({ groups, value, onChange }) {
  const toggle = (code) => onChange(value.includes(code) ? value.filter((x) => x !== code) : [...value, code]);
  const total = Object.values(groups).flat().length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-10 w-full justify-between font-normal" data-testid="syl-boards-select">
          <span className="truncate text-sm">
            {value.length === 0 ? "Select boards…" : `${value.length}/${total || "?"} board${value.length > 1 ? "s" : ""} selected`}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] max-h-[420px] overflow-y-auto p-2">
        {Object.entries(groups).map(([group, items]) => (
          <div key={group} className="mb-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground px-1 mb-1 sticky top-0 bg-popover py-1">
              {group}
            </div>
            {items.map((b) => (
              <label
                key={b.code}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/60 rounded cursor-pointer"
                data-testid={`syl-board-option-${b.code}`}
              >
                <Checkbox checked={value.includes(b.code)} onCheckedChange={() => toggle(b.code)} />
                <span className="text-sm">{b.name}</span>
              </label>
            ))}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}


// ============================================================
// Sprint 19c — Bulk upload + AI-scan tab and extractions panel
// ============================================================

function UploadTabs({ boards, onSingleDone, onBulkDone }) {
  return (
    <Tabs defaultValue="single" className="mb-6" data-testid="syllabus-upload-tabs">
      <TabsList className="mb-3">
        <TabsTrigger value="single" data-testid="syllabus-tab-single">
          <Upload className="h-3.5 w-3.5 mr-1.5" /> Single syllabus
        </TabsTrigger>
        <TabsTrigger value="bulk" data-testid="syllabus-tab-bulk">
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Bulk textbook · AI scan
        </TabsTrigger>
      </TabsList>
      <TabsContent value="single">
        <UploadCard boards={boards} onDone={onSingleDone} />
      </TabsContent>
      <TabsContent value="bulk">
        <BulkUploadCard boards={boards} onDone={onBulkDone} />
      </TabsContent>
    </Tabs>
  );
}

function BulkUploadCard({ boards, onDone }) {
  const fileRef = useRef(null);
  const [institutionId, setInstitutionId] = useState("");
  const [campusIds, setCampusIds] = useState([]);
  const [selectedBoards, setSelectedBoards] = useState([]);
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const instQ = useQuery({ queryKey: ["syl-bulk-insts"], queryFn: () => InstitutionsAPI.list() });
  const campusQ = useQuery({
    queryKey: ["syl-bulk-campuses", institutionId],
    queryFn: () => CampusesAPI.list({ institution_id: institutionId }),
    enabled: !!institutionId,
  });

  const canSubmit = selectedBoards.length > 0 && !!institutionId && !!grade && !!subject && !!file;
  const toggleCampus = (id) => setCampusIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || busy) return;
    if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
      toast.error("Bulk scan requires a PDF file");
      return;
    }
    const fd = new FormData();
    fd.append("institution_id", institutionId);
    // Bulk uses the FIRST selected board (one extraction targets one board)
    fd.append("boards", selectedBoards.join(","));
    fd.append("campus_ids", campusIds.join(","));
    fd.append("grade", grade);
    fd.append("subject", subject);
    fd.append("file", file);
    setBusy(true);
    try {
      const res = await SyllabusAPI.bulkUpload(fd);
      toast.success("Upload accepted — Gemini is scanning the book in the background", { duration: 5000 });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onDone?.(res);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Bulk upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border rounded-2xl shadow-sm p-5 space-y-4" data-testid="syllabus-bulk-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-violet-500/10 text-violet-700 flex items-center justify-center">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <h2 className="font-display font-bold text-lg leading-tight">Bulk textbook scan</h2>
            <p className="text-[11px] text-muted-foreground">
              Upload a full textbook PDF. Gemini extracts chapters &amp; topics so Curriculum auto-suggests them later.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">
            1. Board <span className="text-rose-600">*</span>
          </Label>
          <BoardsMultiSelect groups={boards?.groups || {}} value={selectedBoards} onChange={setSelectedBoards} />
        </div>
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">
            2. Institution <span className="text-rose-600">*</span>
          </Label>
          <Select value={institutionId} onValueChange={(v) => { setInstitutionId(v); setCampusIds([]); }}>
            <SelectTrigger className="h-10" data-testid="syl-bulk-institution">
              <SelectValue placeholder={instQ.isLoading ? "Loading…" : "Select institution"} />
            </SelectTrigger>
            <SelectContent>
              {(instQ.data || []).map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">
            3. Campuses <span className="text-muted-foreground text-[10px] font-normal">(optional · default = all)</span>
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-10 w-full justify-between font-normal" disabled={!institutionId} data-testid="syl-bulk-campuses">
                <span className="truncate text-sm">
                  {!institutionId ? "Select institution first" :
                   campusIds.length === 0 ? "All campuses" :
                   `${campusIds.length} campus${campusIds.length > 1 ? "es" : ""} selected`}
                </span>
                <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] max-h-[280px] overflow-y-auto p-1">
              {(campusQ.data || []).length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                  {institutionId ? "No campuses" : "Select institution first"}
                </div>
              ) : (
                (campusQ.data || []).map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/60 rounded cursor-pointer">
                    <Checkbox checked={campusIds.includes(c.id)} onCheckedChange={() => toggleCampus(c.id)} />
                    <span className="text-sm truncate">{c.name}{c.city ? ` · ${c.city}` : ""}</span>
                  </label>
                ))
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">
            4. Grade <span className="text-rose-600">*</span>
          </Label>
          <Input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="e.g. Grade 7" data-testid="syl-bulk-grade" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">
            5. Subject <span className="text-rose-600">*</span>
          </Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Biology" data-testid="syl-bulk-subject" />
        </div>
      </div>

      <div className="flex items-end gap-3 flex-wrap pt-3 border-t border-border">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block">
            Textbook PDF <span className="text-rose-600">*</span>
            <span className="text-muted-foreground text-[10px] font-normal ml-1">(PDF only · max 25MB)</span>
          </Label>
          <Input
            ref={fileRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="syl-bulk-file"
            className="h-10"
          />
          {file && <div className="text-[11px] text-muted-foreground mt-1">{file.name}</div>}
        </div>
        <Button type="submit" disabled={!canSubmit || busy} className="h-10" data-testid="syl-bulk-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
          Upload &amp; scan
        </Button>
      </div>
    </form>
  );
}

function ExtractionsPanel() {
  const qc = useQueryClient();
  const extQ = useQuery({
    queryKey: ["syllabus-extractions"],
    queryFn: () => SyllabusAPI.extractions(),
    refetchInterval: (q) => {
      // Poll while any extraction is pending/scanning; stop once all are terminal.
      const data = q?.state?.data;
      const items = data?.extractions || [];
      const stillScanning = items.some((e) => ["pending", "scanning"].includes(e.status));
      return stillScanning ? 5000 : false;
    },
  });

  const remove = async (e) => {
    if (!window.confirm(`Delete this scan result? The original syllabus PDF will not be deleted.`)) return;
    try {
      await SyllabusAPI.removeExtraction(e.id);
      toast.success("Scan removed");
      qc.invalidateQueries({ queryKey: ["syllabus-extractions"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const items = extQ.data?.extractions || [];
  if (items.length === 0 && !extQ.isLoading) return null;

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm p-5 mb-6" data-testid="syllabus-extractions-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-violet-600" />
        <h2 className="font-display font-bold text-base">AI-scanned textbooks ({items.length})</h2>
        <span className="text-[11px] text-muted-foreground">Curriculum auto-suggests chapters &amp; topics from these.</span>
      </div>
      {extQ.isLoading ? (
        <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
      ) : (
        <div className="divide-y divide-border border border-border rounded-md overflow-hidden">
          {items.map((e) => <ExtractionRow key={e.id} ex={e} onRemove={() => remove(e)} />)}
        </div>
      )}
    </div>
  );
}

function ExtractionStatusPill({ status, chapterCount }) {
  if (status === "done") {
    return <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20 border-0 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />Scanned · {chapterCount} chapters</Badge>;
  }
  if (status === "failed") {
    return <Badge className="bg-rose-500/15 text-rose-700 hover:bg-rose-500/20 border-0 text-[10px]"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>;
  }
  return <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/20 border-0 text-[10px]"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Scanning…</Badge>;
}

function ExtractionRow({ ex, onRemove }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="px-4 py-3 hover:bg-muted/40" data-testid={`syllabus-extraction-${ex.id}`}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-violet-500/10 text-violet-600 flex items-center justify-center shrink-0">
          <FileText className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{ex.grade} · {ex.subject}</span>
            <Badge variant="outline" className="text-[10px]">{(ex.board || "").toUpperCase()}</Badge>
            <ExtractionStatusPill status={ex.status} chapterCount={ex.chapter_count} />
          </div>
          {ex.status === "failed" && ex.error && (
            <div className="text-[11px] text-rose-700 mt-1 line-clamp-2">{ex.error}</div>
          )}
          {ex.status === "done" && ex.chapters && ex.chapters.length > 0 && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="text-[11px] text-primary mt-1 hover:underline"
              data-testid={`syllabus-extraction-toggle-${ex.id}`}
            >
              {open ? "Hide" : "Show"} chapters
            </button>
          )}
          {open && (
            <ul className="mt-2 space-y-1.5 text-[12px]">
              {ex.chapters.map((ch, i) => (
                <li key={i} className="border border-border rounded-md p-2">
                  <div className="font-semibold">{ch.name}</div>
                  {ch.topics && ch.topics.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {ch.topics.map((t, j) => (
                        <span key={j} className="text-[10px] bg-sky-500/10 text-sky-700 px-1.5 py-0.5 rounded">{t}</span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button size="icon" variant="ghost" onClick={onRemove} title="Delete scan" data-testid={`syllabus-extraction-delete-${ex.id}`}>
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </Button>
      </div>
    </div>
  );
}
